<template>
  <div
    class="it-ops-story project-case-study"
    :style="{ '--case-chrome': chromeHeight + 'px' }"
  >
    <div class="case-hero case-hero-text">
      <ProjectPageHeader project-id="pwcItOps" context="PwC">
        <h1>AI for IT Operations</h1>
        <p class="project-subtitle">
          Maintaining technical knowledge and carrying out service requests
        </p>
      </ProjectPageHeader>
      <div class="case-hero-lede">
        <p class="lede">
          At PwC, I designed a system that uses AI to identify gaps in an IT
          assistant’s knowledge, research the missing information and keep its
          knowledge base up to date. I also built a browser agent that
          interprets service tickets and carries out tasks in the applications
          the team already uses.
        </p>
      </div>
    </div>
    <ProjectChapterNav
      ref="chapterNav"
      :chapters="chapters"
      :start-number="0"
      :active-chapter="activeChapter"
      :progress="chapterProgress"
      @navigate="goToChapter"
    />
    <article class="project-narrative case-narrative">
      <section id="case-context" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>00</span>Context</span>
          <h2
            id="ai-in-it-operations"
            data-source-anchor="ai-in-it-operations"
          >
            Where AI can help in IT operations
          </h2>
        </header>
        <div class="case-reading case-copy" data-case-reveal>
          <p>
            Keeping an organisation’s technology running involves maintaining
            systems, investigating faults and supporting the people who use them.
            An IT team works across many products and has to keep up as their
            software and documentation change. At PwC, I researched how AI could
            help with this work, including making technical knowledge easier to
            consult and automating tasks in existing applications.
          </p>
          <p>
            Two kinds of work need different support. Answering a technical question depends on reliable documentation. Fulfilling a service ticket depends on acting in an application and checking the result. I connected AI reasoning to those workflows, retaining source records for knowledge updates and operator controls for browser actions.
          </p>
        </div>
      </section>
      <section id="case-chapter-0" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>01</span>Knowledge management</span>
          <h2
            id="giving-the-question-answering-assistant-better-knowledge"
            data-source-anchor="knowledge-management"
          >
            Keeping the assistant’s knowledge useful
          </h2>
        </header>
        <div class="case-reading case-copy" data-case-reveal>
          <p>
            The IT assistant answers questions about using and maintaining systems. It retrieves relevant passages from technical documents and supplies them to a language model when composing an answer. This approach is known as retrieval-augmented generation (RAG).
          </p>
          <p>
            That collection, its knowledge base, needs continuing attention.
            Useful information may be missing, and documents already collected
            can become outdated. I designed the platform’s Knowledge Management
            around these two needs, acquiring material that expands what the
            assistant can answer and maintaining it as the sources change.
          </p>
        </div>
        <div class="case-subsection" data-case-reveal>
          <h3 id="where-the-assistant-needs-more-knowledge">
            Where does the assistant need more knowledge?
          </h3>
          <div class="case-copy">
            <p>
            I used specific questions to identify gaps in the knowledge base. Guidance on renewing a security certificate, for example, might omit the steps that make an application use the replacement. Checking each question against the collected documents gives the next search a precise target.
          </p>
            <p>
            Unanswered user questions reveal immediate needs. I also used AI to propose questions people might ask next, then checked whether the existing material could answer them. This lets the collection develop ahead of demand while making use of knowledge already held.
          </p>
            <p>
            Documentation collected for Python web apps may also explain how to host a REST API. The matrix below therefore separates whether a question has been asked from whether the knowledge base can answer it. Select a case to see an example, then research the missing answers.
          </p>
          </div>
        </div>
        <div class="case-wide" data-case-reveal>
          <ItOpsJourney
            id="certificate-knowledge-journey"
            class="ops-wide"
            kind="knowledge"
          />
        </div>
        <div class="case-subsection" data-case-reveal>
          <h3 id="learning-from-the-questions-the-assistant-cannot-answer">
            Researching gaps in technical guidance
          </h3>
          <div class="case-copy">
            <p>
              Several knowledge gaps can concern the same problem.
              Certificate expiry, renewal and a replacement that fails to take
              effect all concern the certificate lifecycle. I grouped related
              gaps into a shared research objective, giving the agent a reason
              to explore their connections. The individual questions stayed
              attached to the objective as concrete checks on what the
              research needed to cover.
            </p>
            <p>
              That objective guided where to look. I used AI to find relevant
              starting pages, then directed a crawler to follow links and
              collect documentation that could answer the questions. Starting
              near the relevant material mattered on large vendor sites,
              where following links from the homepage could consume the search
              budget before reaching the guidance needed.
            </p>
            <p>
              As it collected material, the agent assessed which questions
              now had supporting evidence and what each additional page
              contributed. An unresolved question could lead it to look for
              another starting page. Once the questions were covered and
              further crawling added little useful information, it could send
              the proposed sources for review. Combining question coverage
              with information gain gave the research a stopping condition,
              alongside page and time limits.
            </p>
          </div>
        </div>
        <div class="case-subsection" data-case-reveal>
          <h3 id="keeping-answers-connected-to-their-sources">
            What happens when the guidance changes?
          </h3>
          <div class="case-copy">
            <p>
              Adding a document to the knowledge base creates an ongoing
              maintenance task. The vendor may revise its instructions while
              the assistant still draws on the earlier copy. To check for
              those changes, the system needs to know where each document came
              from. I retained the original source URL when collecting the
              material so it could be revisited later.
            </p>
            <p>
              The ingestion pipeline prepares documents for the knowledge
              base by extracting their text and splitting it into searchable
              passages, or chunks. After this processing, a source update
              needs to be connected to the passages it affects. I stored the
              accepted version of each source alongside links to its derived
              knowledge. These records formed the <strong>Source Registry</strong>,
              giving the system both a version to compare against and a way
              to locate the knowledge that might need updating.
            </p>
            <p>
              Monitoring could then revisit the original URL and compare its
              main content with the accepted snapshot. Normalising the content
              before comparison filters out differences in formatting. If the
              content has changed, AI reads the difference alongside the linked
              knowledge and assesses its significance. A changed page may
              contain an updated procedure, or the change may have no bearing on
              the guidance already held. Detecting the difference and judging
              its implications require different kinds of work.
            </p>
            <p>
              Because the affected knowledge is traceable, the agent can propose
              replacing or removing specific passages, or request further
              research if the changed source leaves a question unresolved.
              Proposed updates go through human review before the ingestion
              pipeline updates the knowledge and its source records together.
              The next check then compares against the newly accepted version,
              preserving the connection between what the assistant knows and the
              evidence supporting it.
            </p>
          </div>
        </div>
        <div class="case-reading case-technical-notes" data-case-reveal>
          <details class="implementation-notes" :open="knowledgeTechnicalOpen">
            <summary>
              <span>Inside Knowledge Management</span
              ><span
                >Research, ingestion, and source records
                <span aria-hidden="true">+</span></span
              >
            </summary>
            <div>
              <h3
                id="from-a-source-to-maintained-knowledge"
                data-source-anchor="from-a-source-to-maintained-knowledge"
              >
                Processing and maintaining source material
              </h3>
              <p>
                The crawler research covered Crawl4AI Adaptive, SiteOne,
                Mdream/crawl, Browsertrix, and Firecrawl OSS across Azure, SAP,
                ServiceNow, and Oracle documentation. The results informed the
                separation between deciding what knowledge to acquire and the
                mechanics of fetching and processing pages.
              </p>
              <p>
                For each candidate page, the acquisition agent assesses
                relevance to the objective, source authority, overlap with
                material already collected, and likely information gain. Seed
                discovery supplies new starting URLs when the existing crawl
                cannot cover a question. The agent retains the original
                questions alongside the broader objective, allowing coverage to
                be checked as the search proceeds.
              </p>
              <p>
                Approved material passes through a shared ingestion and update
                pipeline for extraction, normalisation, deduplication, chunking,
                and indexing. Provenance records connect the searchable chunks
                to their source. The pipeline updates the Knowledge Base and
                Source Registry together for additions, replacements, and
                removals.
              </p>
              <p>
                The accepted snapshot is the baseline for monitoring. It
                advances when a reviewed update is applied to the knowledge
                base, so later comparisons remain tied to the guidance the
                assistant is using. Source fetching, normalisation, comparison,
                and record updates use conventional system logic. AI assesses
                what a detected change means for the linked knowledge.
              </p>
              <div class="system-connection">
                <span>Knowledge Base</span
                ><span aria-hidden="true"
                  ><SiteIcon name="arrows-horizontal" /></span
                ><span>Source Registry</span
                ><span class="return-path"
                  >Searchable knowledge stays linked to its accepted source
                  material</span
                >
              </div>
            </div>
          </details>
        </div>
      </section>
      <section id="case-chapter-1" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>02</span>Browser agent</span>
          <h2
            id="let-the-ai-work-through-the-request"
            data-source-anchor="browser-control"
          >
            Carrying out a service ticket in the browser
          </h2>
        </header>
        <div class="case-reading case-copy" data-case-reveal>
          <p>
            The second area concerned service requests that an IT operator
            carries out in a browser. A ticket such as “Please give Alex Chen
            editor access to the Analytics workspace” describes an outcome.
            The operator still has to find the right account, workspace and
            permission in the application. I built the platform’s Browser
            Control agent to interpret the ticket and carry out that work,
            reading the current page to decide what to do next.
          </p>
          <p>
            I brought the agent into the operator’s existing browser through an
            extension. The agent works in a dedicated tab. Interaction cards
            beside the application let the operator make decisions and supply
            inputs when needed. The task continues from the current page after
            either the agent or the
            operator acts.
          </p>
          <p>
            Try the example ticket below to follow the exchange between the
            agent and operator. After it is resolved, open the ticket record to
            review the actions, approvals, and outcome.
          </p>
        </div>
        <div class="case-wide" data-case-reveal>
          <ItOpsJourney
            id="browser-control-journey"
            class="ops-wide"
            kind="browser"
          />
        </div>
        <div class="case-subsection" data-case-reveal>
          <h3 id="why-bring-ai-into-the-browser">
            Turning the request into actions
          </h3>
          <div class="case-copy">
            <p>
              Even a familiar task can unfold differently when an application
              shows a validation message or an operator edits a form. The agent
              needs to see the effect of each action before choosing the next
              one. I used an observation loop that reads the current page,
              chooses an action in relation to the ticket, and reads the result.
              The ticket supplies the intended outcome while the page supplies
              the information needed to work towards it.
            </p>
            <p>
              When a person intervenes, the resulting page becomes the starting
              point for the next decision. I kept the observations, actions, and
              human decisions with the paused task so the agent could continue
              after sign-in or a correction without losing the context of
              earlier work.
            </p>
          </div>
        </div>
        <div class="case-subsection" data-case-reveal>
          <h3 id="choosing-an-architecture-people-can-work-with">
            Why a browser extension?
          </h3>
          <div class="case-copy">
            <p>
              The agent needed access to internal applications and a place for
              the operator to work alongside it. A remote browser would need its own
              access to internal systems and a way for the operator to
              intervene. A local automation service could use the operator’s
              environment, but it would need separate installation and
              maintenance. I compared those options with an extension that could combine control and
              interaction inside Chrome or Edge.
            </p>
          </div>
        </div>
        <div class="case-wide" data-case-reveal>
          <BrowserArchitectureComparison />
        </div>
        <div class="case-copy case-reading" data-case-reveal>
          <p>
            The extension combined browser automation and the operator interface
            in one installation. It prepared changes and displayed interaction
            cards beside the application. Sign-in and multi-factor
            authentication stayed in the browser the operator already used. This
            made taking over part of the normal task flow.
          </p>
          <p>
            I placed reasoning and task state in a central service, so updates
            to the agent’s planning logic could reach everyone through one
            deployment. The extension handled access to the local browser and
            interaction with the operator.
          </p>
        </div>
        <div class="case-subsection" data-case-reveal>
          <h3
            id="keeping-the-work-visible"
            data-source-anchor="a-place-to-start-intervene-and-look-back"
          >
            Checking the result and keeping a task record
          </h3>
          <div class="case-copy">
            <p>
              After executing a request, the agent checks the application’s
              confirmation and records the outcome alongside the actions and
              human decisions that led to it. The operator can return to the
              ticket on the dashboard to review the completed work.
            </p>
          </div>
        </div>
        <div class="case-reading case-technical-notes" data-case-reveal>
          <details class="implementation-notes" :open="browserTechnicalOpen">
            <summary>
              <span>Inside Browser Control</span
              ><span
                >Planning, browser actions, and task history
                <span aria-hidden="true">+</span></span
              >
            </summary>
            <div>
              <h3
                id="from-planning-to-a-browser-action"
                data-source-anchor="from-planning-to-a-browser-action"
              >
                The browser agent’s execution loop
              </h3>
              <p>
                A FastAPI service manages browser tasks. LangGraph retains the
                request, page observations, action history, and human decisions
                as the planner moves between steps. Pausing for approval or
                manual input preserves the same task so it can resume
                afterwards.
              </p>
              <p>
                Playwright sends structured actions through a relay to the
                browser extension. The extension uses the Chrome DevTools
                Protocol to read and control the dedicated tab, then returns the
                resulting page state to the cloud agent. The extension runs on
                the operator’s machine and connects to centrally hosted
                automation and reasoning services.
              </p>
              <div class="system-connection">
                <span>Request + current page</span
                ><span aria-hidden="true"><SiteIcon name="arrow-right" /></span
                ><span>Agent decision</span
                ><span aria-hidden="true"><SiteIcon name="arrow-right" /></span
                ><span>Browser action</span
                ><span class="return-path"
                  >Read the resulting page, then decide what to do next</span
                >
              </div>
            </div>
          </details>
        </div>
      </section>
      <section id="case-chapter-2" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>03</span>Reflections</span>
          <h2
            id="where-judgement-changes-hands"
            data-source-anchor="where-judgement-changes-hands"
          >
            What to automate, and how
          </h2>
        </header>
        <div class="case-reading case-copy" data-case-reveal>
          <p>
            Across both parts of the platform, I approached automation at the
            level of individual decisions within a task. Researching a question,
            proposing an update, accepting the
            guidance and applying the change each require different information
            and judgement. In knowledge maintenance, I assigned investigation
            and preparation to AI, gave the reviewer a concrete proposal with
            its evidence, and used the ingestion pipeline to apply the accepted
            change.
          </p>
          <p>
            Delegating a step to AI also requires deciding which parts of its method to
            specify. A prompt can give a model an objective, evidence and criteria for judging
            its next step. A more structured workflow can prescribe stages and available tools
            while leaving particular decisions to the model. Where the inputs and procedure
            are already well defined, a script can execute the same logic each time. A
            workflow can combine these approaches and give the model different amounts of
            freedom at each step.
          </p>
          <p>
            In knowledge acquisition, I specified what the search should
            achieve before knowing which pages it would need. The
            original questions define the coverage required, and the broader
            research objective gives the agent room to follow related evidence.
            Relevance, source authority and information gain guide its choices.
            This structure keeps the research connected to the missing
            knowledge while allowing the route through the documentation to
            emerge as the agent reads.
          </p>
          <p>
            I used prompts to give the model criteria for weighing evidence,
            and system logic to enforce fixed conditions. Tool schemas define
            the actions the system can accept,
            and page and time limits bound execution. Predictable operations
            such as comparing normalised snapshots and updating source records belong in system
            logic. In source monitoring, that comparison determines whether
            there is a change for AI to assess at all. The model then reasons
            about its implications for the linked knowledge. This concentrates
            reasoning on the decision that needs interpretation.
          </p>
          <p>
            Human-in-the-loop design adds a choice about responsibility and
            context. A person can authorise a well-understood change, supply
            information the agent lacks, or correct its interpretation. Each
            requires the relevant evidence and a way to continue from the
            person's decision. I kept source changes connected to their
            affected passages, and retained task history and current page state
            when an operator intervened. Those records make review and
            correction part of the workflow, so handing work back to AI
            preserves what the person has just contributed.
          </p>
          <p>
            How much structure to provide can also change as the work becomes
            better understood. Repeated cases may reveal a rule that can be
            written into guidance. If applying it still requires interpreting
            the situation, it can guide the model's judgement. If its conditions
            and exceptions can be stated precisely, it becomes a candidate for
            a decision table or script. I see this as part of developing the
            automation over time, making established knowledge reusable while
            keeping reasoning available where the next decision still depends
            on context.
          </p>
        </div>
      </section>
    </article>
  </div>
</template>
<script>
import ProjectPageHeader from "./ui/ProjectPageHeader.vue";
import projectCaseStudyMixin from "../mixins/projectCaseStudyMixin";
import ProjectChapterNav from "./ui/ProjectChapterNav.vue";
import ItOpsJourney from "./view/ItOpsJourney.vue";
import BrowserArchitectureComparison from "./view/BrowserArchitectureComparison.vue";
export default {
  computed: {
    chapters() {
      return [
        { id: "case-context", label: "Context" },
        { id: "case-chapter-0", label: "Knowledge management" },
        { id: "case-chapter-1", label: "Browser agent" },
        { id: "case-chapter-2", label: "Reflections" },
      ];
    },
  },
  name: "ItOperationsPage",
  components: {
    ProjectChapterNav,
    ProjectPageHeader,
    ItOpsJourney,
    BrowserArchitectureComparison,
  },
  mixins: [projectCaseStudyMixin],
  data() {
    return { knowledgeTechnicalOpen: false, browserTechnicalOpen: false };
  },
  watch: {
    "$route.hash": {
      immediate: true,
      handler(hash) {
        if (hash === "#from-planning-to-a-browser-action")
          this.browserTechnicalOpen = true;
        if (hash === "#from-a-source-to-maintained-knowledge")
          this.knowledgeTechnicalOpen = true;
      },
    },
  },
};
</script>
<style scoped>
.it-ops-story {
  --ops-ink: #343434;
  --ops-muted: #737373;
  --ops-line: #dedede;
  --ops-accent: #343434;
  max-width: var(--site-content-width);
  color: var(--ops-ink);
}
.it-ops-story h1 {
  font-size: 2rem;
}
.it-ops-story h2 {
  scroll-margin-top: 7rem;
}
.it-ops-story article > p,
.it-ops-story section p {
  color: var(--ops-ink);
}
.lede {
  font-size: 1.12em;
  line-height: 1.75;
}
.it-ops-story section h3 {
  line-height: 1.45;
  scroll-margin-top: 7rem;
}
.implementation-notes {
  margin: 2.5rem 0;
  border: 0;
}
.implementation-notes > summary {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  padding: 20px 0;
  cursor: pointer;
  list-style: none;
  font: 12px/1.5 "Quattrocento Sans", sans-serif;
}
.implementation-notes > summary::-webkit-details-marker {
  display: none;
}
.implementation-notes > summary > span:first-child {
  font-weight: 700;
  color: var(--ops-accent);
}
.implementation-notes > summary > span:last-child {
  color: var(--ops-muted);
}
.implementation-notes > summary > span:last-child > span {
  display: inline-block;
  margin-left: 12px;
  font-size: 16px;
  transition: transform 200ms ease;
}
.implementation-notes[open] > summary > span:last-child > span {
  transform: rotate(45deg);
}
.implementation-notes > div {
  padding-bottom: 24px;
}
.implementation-notes h3 {
  margin: 20px 0 10px;
  font-size: 1em;
  scroll-margin-top: 7rem;
}
.implementation-notes p {
  font-size: 0.88em;
  line-height: 1.75;
}
.system-connection {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 20px 10px;
  border: 1px solid var(--ops-line);
  margin: 20px 0;
  font: 12px/1.6 "Quattrocento Sans", sans-serif;
}
.system-connection > span[aria-hidden] {
  color: var(--ops-accent);
}
.return-path {
  flex-basis: 100%;
  text-align: center;
  font-size: 10px;
  color: var(--ops-muted);
}
.it-ops-story button:focus-visible,
.it-ops-story summary:focus-visible {
  outline: 2px solid var(--ops-accent);
  outline-offset: 5px;
}
:global(body.dark-mode .it-ops-story) {
  --ops-ink: #dedede;
  --ops-muted: #a2a2a2;
  --ops-line: #444444;
  --ops-accent: #dedede;
}
.ops-wide {
  width: 100%;
  margin-inline: 0;
}
@media (max-width: 600px) {
  .implementation-notes > summary {
    align-items: start;
  }
  .implementation-notes > summary > span:last-child {
    max-width: 160px;
    text-align: right;
  }
}
@media (prefers-reduced-motion: reduce) {
  .implementation-notes > summary > span:last-child > span {
    transition: none;
  }
}
</style>
